import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connector } from '../../services/api';
import { getErrorMessage } from '../../utils/errorUtils';
import { isBaseModelUnsupportedForFineTuning } from '../../utils/gpt5ConfiguratorModels';
import { normalizeTrainingIntegrationAgents } from '../../utils/trainingIntegrationAgents';
import type { AgentConfiguratorState } from '../../types/agentConfigurator';
import ConfirmModal from '../ConfirmModal';
import { NOTIFICATION_AUTO_HIDE_MS } from '../../constants/uiTiming';
import {
  clampHistoryDepth,
  commitPositiveIntegerDraft,
  numberInputDisplay,
  sanitizePositiveIntegerDraft,
} from '../../utils/numberInputDraft';
import {
  HISTORY_DEPTH_MAX,
  HISTORY_DEPTH_MIN,
} from '../../constants/datasetGenerationLimits';

interface AutoConfigAgent {
  agent_id?: string;
  agent_name: string;
  dataset_id?: string;
  base_model?: string;
  is_trainable: boolean;
  filters?: { reactions?: string[]; comment?: boolean; groundtruth?: boolean };
}

interface AutoConfig {
  id: string;
  integration_id: string;
  enabled: boolean;
  schedule: string;
  regime: string;
  regime_percent: number;
  period_days: number;
  history_depth?: number;
  model_name_prefix: string;
  agents: AutoConfigAgent[];
  next_run_at: string | null;
  last_run_at: string | null;
  last_job_id: string | null;
  last_status: string | null;
  last_error: string | null;
  last_error_details: string | Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export type AgentReaction = { like: boolean; dislike: boolean };

interface Props {
  integrationId: string | null;
  /** Конфигурация агентов из конфигуратора (Coordinator + Workers) — подставляется в форму создания конфига */
  configFromConfigurator?: AgentConfiguratorState | null;
  /** Реакции лайк/дизлайк по имени агента — синхронизируются с модалкой Training Job */
  agentReactions?: Record<string, AgentReaction>;
  /** Обновление реакций (поднимаем в ConnectorPage) */
  onAgentReactionsChange?: (reactions: Record<string, AgentReaction>) => void;
}

const SCHEDULES = ['hourly', 'daily', 'weekly', 'monthly'];
const REGIMES = ['no_overlap', 'overlap', 'mix_old'];
const MODEL_NAME_MAX_LENGTH = 100;
const AUTO_CONFIG_POLL_INTERVAL_MS = 25_000;
const DEFAULT_HISTORY_DEPTH = 10;

type AgentFiltersShape = { reactions?: string[]; comment?: boolean; groundtruth?: boolean };

/** При изменении только reactions не теряем comment / groundtruth */
function normalizeAgentFilters(prev: AgentFiltersShape | undefined, reactions: string[]): AgentFiltersShape | undefined {
  const comment = prev?.comment ?? false;
  const groundtruth = prev?.groundtruth ?? false;
  if (reactions.length === 0 && !comment && !groundtruth) return undefined;
  return { reactions, comment, groundtruth };
}

/** Текст в скобках рядом с именем агента: реакции + Comments + Groundtruth */
function formatAgentFiltersBracket(filters?: AgentFiltersShape): string | null {
  if (!filters) return null;
  const parts: string[] = [];
  for (const r of filters.reactions || []) {
    const low = r.toLowerCase();
    const label =
      low === 'like'
        ? 'Like'
        : low === 'dislike'
          ? 'Dislike'
          : r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    parts.push(label);
  }
  if (filters.comment) parts.push('Comments');
  if (filters.groundtruth) parts.push('Groundtruth');
  if (parts.length === 0) return null;
  return `(${parts.join(', ')})`;
}

const AutoFineTuningPanel: React.FC<Props> = ({
  integrationId,
  configFromConfigurator = null,
  agentReactions = {},
  onAgentReactionsChange,
}) => {
  const [configs, setConfigs] = useState<AutoConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [runningConfigId, setRunningConfigId] = useState<string | null>(null);
  const [integrationAgents, setIntegrationAgents] = useState<any[]>([]);
  const [connectorDatasets, setConnectorDatasets] = useState<Array<{ id: string; label: string }>>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; configId: string | null }>({ isOpen: false, configId: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const editFormRef = useRef<HTMLDivElement | null>(null);
  const isPollingLoadInFlightRef = useRef(false);

  type FormAgent = { agent_id?: string; agent_name: string; dataset_id?: string; base_model: string; is_trainable: boolean; filters?: { reactions?: string[]; comment?: boolean; groundtruth?: boolean } };
  const [form, setForm] = useState({
    enabled: true,
    schedule: 'daily',
    regime: 'no_overlap',
    regime_percent: 20,
    period_days: 30,
    history_depth: DEFAULT_HISTORY_DEPTH as number | string,
    model_name_prefix: '',
    dataset_id: '',
    agents: [] as FormAgent[],
  });

  /** Free-text drafts so users can clear the field and type multi-digit values (e.g. 20, 30) without NaN glitches. */
  const [regimePercentDraft, setRegimePercentDraft] = useState('20');
  const [periodDaysDraft, setPeriodDaysDraft] = useState('30');

  const clampRegimePercent = (n: number) => Math.min(90, Math.max(0, Math.round(n)));
  const clampPeriodDays = (n: number) => Math.min(365, Math.max(1, Math.round(n)));

  const applyRegimePercentDraft = () => {
    const raw = regimePercentDraft.trim();
    if (raw === '') {
      const v = 20;
      setForm((p) => ({ ...p, regime_percent: v }));
      setRegimePercentDraft(String(v));
      return;
    }
    const n = Number(raw.replace(',', '.'));
    if (!Number.isFinite(n)) {
      setRegimePercentDraft(String(form.regime_percent));
      return;
    }
    const v = clampRegimePercent(n);
    setForm((p) => ({ ...p, regime_percent: v }));
    setRegimePercentDraft(String(v));
  };

  const applyPeriodDaysDraft = () => {
    const raw = periodDaysDraft.trim();
    if (raw === '') {
      const v = 30;
      setForm((p) => ({ ...p, period_days: v }));
      setPeriodDaysDraft(String(v));
      return;
    }
    const n = Number(raw.replace(',', '.'));
    if (!Number.isFinite(n)) {
      setPeriodDaysDraft(String(form.period_days));
      return;
    }
    const v = clampPeriodDays(n);
    setForm((p) => ({ ...p, period_days: v }));
    setPeriodDaysDraft(String(v));
  };

  const clearSuccess = () => setTimeout(() => setSuccess(null), NOTIFICATION_AUTO_HIDE_MS);

  const loadConfigs = useCallback(async (opts?: { silent?: boolean }) => {
    if (!integrationId) return;
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const resp = await connector.listAutoConfigs({ integration_id: integrationId! });
      const data = resp?.data;
      setConfigs(Array.isArray(data) ? data : (data?.items || []));
    } catch (e: any) {
      if (!silent) {
        setError(getErrorMessage(e, 'Failed to load auto configs'));
      }
      setConfigs([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [integrationId]);

  const loadAgents = useCallback(async () => {
    if (!integrationId) return;
    try {
      const resp = await connector.listIntegrationAgents(integrationId);
      setIntegrationAgents(normalizeTrainingIntegrationAgents(resp?.data));
    } catch {
      setIntegrationAgents([]);
    }
  }, [integrationId]);

  const loadDatasets = useCallback(async () => {
    if (!integrationId) return;
    setDatasetsLoading(true);
    try {
      const resp = await connector.getDatasets(integrationId);
      const list = resp?.data ?? [];
      const normalized = Array.isArray(list) ? list : [];
      setConnectorDatasets(normalized.map((d: any) => ({ id: String(d.id), label: String(d.label || d.id) })));
    } catch {
      setConnectorDatasets([]);
    } finally {
      setDatasetsLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    if (integrationId) {
      void loadConfigs();
      void loadAgents();
      void loadDatasets();
    } else {
      setConfigs([]);
      setIntegrationAgents([]);
      setConnectorDatasets([]);
    }
  }, [integrationId, loadConfigs, loadAgents, loadDatasets]);

  useEffect(() => {
    if (!integrationId) return;
    const intervalId = window.setInterval(() => {
      if (document.hidden) return;
      if (isPollingLoadInFlightRef.current) return;
      isPollingLoadInFlightRef.current = true;
      void loadConfigs({ silent: true }).finally(() => {
        isPollingLoadInFlightRef.current = false;
      });
    }, AUTO_CONFIG_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [integrationId, loadConfigs]);

  useEffect(() => {
    if (showCreateForm && editingConfigId && editFormRef.current) {
      editFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showCreateForm, editingConfigId]);

  useEffect(() => {
    if (!showCreateForm) return;
    setRegimePercentDraft(String(form.regime_percent));
    setPeriodDaysDraft(String(form.period_days));
  }, [showCreateForm, editingConfigId, form.regime_percent, form.period_days]);

  const handleToggle = async (config: AutoConfig) => {
    if (!integrationId) return;
    try {
      await connector.updateAutoConfig(config.id, integrationId, { enabled: !config.enabled });
      setSuccess(`Config ${config.enabled ? 'disabled' : 'enabled'}`);
      clearSuccess();
      await loadConfigs();
    } catch (e: any) {
      setError(getErrorMessage(e, 'Failed to update config'));
    }
  };

  const handleDelete = async (configId: string) => {
    if (!integrationId) return;
    setDeleteConfirm({ isOpen: true, configId });
  };

  const confirmDeleteConfig = async () => {
    const configId = deleteConfirm.configId;
    if (!integrationId || !configId) return;
    setDeleteLoading(true);
    try {
      await connector.deleteAutoConfig(configId, integrationId);
      setDeleteConfirm({ isOpen: false, configId: null });
      setSuccess('Config deleted');
      clearSuccess();
      await loadConfigs();
    } catch (e: any) {
      setError(getErrorMessage(e, 'Failed to delete config'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRun = async (configId: string) => {
    if (!integrationId) return;
    const cfg = configs.find(c => c.id === configId);
    if (!cfg) return;
    if ((cfg.model_name_prefix?.length ?? 0) > MODEL_NAME_MAX_LENGTH) {
      setError(`Model name prefix must be at most ${MODEL_NAME_MAX_LENGTH} characters. Edit the config to shorten it.`);
      return;
    }
    const trainableAgents = (cfg.agents || []).filter(a => a.is_trainable !== false);
    if (trainableAgents.length === 0) {
      setError('No trainable agents in this config. Add at least one agent with fine-tuning enabled.');
      return;
    }
    setRunningConfigId(configId);
    try {
      const now = new Date();
      const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
      const from = new Date(to);
      from.setUTCDate(from.getUTCDate() - Math.max(1, cfg.period_days || 30));
      from.setUTCHours(0, 0, 0, 0);
      const previewAgents = trainableAgents.map(a => ({
        agent_id: (a.agent_id || a.agent_name || '').trim() || a.agent_name,
        agent_name: a.agent_name,
        dataset_id: a.dataset_id,
        base_model: a.base_model,
        is_trainable: true,
        filters: a.filters ? { reactions: a.filters.reactions || [], comment: a.filters.comment ?? false, groundtruth: a.filters.groundtruth ?? false } : undefined,
      }));
      await connector.previewTrainingJob({
        integration_id: integrationId,
        model_name: (cfg.model_name_prefix || 'auto').trim(),
        group_by_base_model: true,
        history_depth: clampHistoryDepth(cfg.history_depth, DEFAULT_HISTORY_DEPTH),
        training_period: { from: from.toISOString(), to: to.toISOString() },
        agents: previewAgents,
      });
      await connector.runAutoConfig(configId, integrationId);
      setSuccess('Manual run started');
      clearSuccess();
      await loadConfigs();
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      const details = data?.details as { failed_agents?: Array<{ agent_name?: string; error?: string }> } | undefined;
      const failedAgents = details?.failed_agents;
      if (status === 400 && Array.isArray(failedAgents) && failedAgents.length > 0) {
        const mainMsg = data?.message || 'None of the selected agents could be started for fine-tuning.';
        const list = failedAgents.map(a => `${a.agent_name || 'Agent'}: ${a.error || ''}`).join('\n');
        setError(`${mainMsg}\n\n${list}`);
      } else {
        setError(getErrorMessage(e, 'Failed to start manual run'));
      }
    } finally {
      setRunningConfigId(null);
    }
  };

  const handleCreate = async () => {
    if (!integrationId) return;
    if (!form.model_name_prefix.trim()) {
      setError('Model name prefix is required');
      return;
    }
    if (form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH) {
      setError(`Model name prefix must be at most ${MODEL_NAME_MAX_LENGTH} characters`);
      return;
    }
    const trainableAgents = form.agents.filter(a => a.is_trainable);
    if (trainableAgents.length === 0) {
      setError('At least one agent must be trainable');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resolveAgentIdForCreate = (a: FormAgent): string => {
        const id = (a.agent_id && a.agent_id.trim()) || '';
        if (id) return id;
        const fromConfig = getAgentIdFromConfigurator(a.agent_name);
        if (fromConfig) return fromConfig;
        return a.agent_name === 'Coordinator' ? 'coordinator' : `agent-${a.agent_name}`;
      };
      const createAgents = form.agents.filter(a => a.is_trainable).map(a => {
        const reactions = a.filters?.reactions ?? [];
        return {
          agent_id: resolveAgentIdForCreate(a),
          agent_name: a.agent_name,
          dataset_id: form.dataset_id || undefined,
          base_model: a.base_model,
          is_trainable: true,
          filters: {
            reactions,
            comment: a.filters?.comment ?? false,
            groundtruth: a.filters?.groundtruth ?? false,
          },
        };
      });
      await connector.createAutoConfig({
        integration_id: integrationId,
        enabled: true,
        schedule: form.schedule,
        regime: form.regime,
        regime_percent: form.regime_percent,
        period_days: form.period_days,
        history_depth: clampHistoryDepth(form.history_depth, DEFAULT_HISTORY_DEPTH),
        model_name_prefix: form.model_name_prefix,
        agents: createAgents,
      });
      setSuccess('Auto config created');
      clearSuccess();
      setShowCreateForm(false);
      setForm({
        enabled: true,
        schedule: 'daily',
        regime: 'no_overlap',
        regime_percent: 20,
        period_days: 30,
        history_depth: DEFAULT_HISTORY_DEPTH,
        model_name_prefix: '',
        dataset_id: '',
        agents: [],
      });
      await loadConfigs();
    } catch (e: any) {
      setError(getErrorMessage(e, 'Failed to create auto config'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!integrationId || !editingConfigId) return;
    if (!form.model_name_prefix.trim()) {
      setError('Model name prefix is required');
      return;
    }
    if (form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH) {
      setError(`Model name prefix must be at most ${MODEL_NAME_MAX_LENGTH} characters`);
      return;
    }
    const trainableAgents = form.agents.filter(a => a.is_trainable);
    if (trainableAgents.length === 0) {
      setError('At least one agent must be trainable');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      /** Resolve agent_id from form, then from integration config; backend requires ids that exist in integration config. */
      const resolveAgentId = (a: FormAgent): string => {
        const id = (a.agent_id && a.agent_id.trim()) || '';
        if (id) return id;
        const fromConfig = getAgentIdFromConfigurator(a.agent_name);
        if (fromConfig) return fromConfig;
        return a.agent_name === 'Coordinator' ? 'coordinator' : `agent-${a.agent_name}`;
      };
      const agentsPayload = form.agents.filter(a => a.is_trainable).map(a => {
        const hasReactions = (a.filters?.reactions?.length ?? 0) > 0;
        const filters =
          hasReactions || a.filters?.comment !== undefined || a.filters?.groundtruth !== undefined
            ? {
                reactions: a.filters?.reactions ?? [],
                comment: a.filters?.comment,
                groundtruth: a.filters?.groundtruth,
              }
            : undefined;
        return {
          agent_id: resolveAgentId(a),
          agent_name: a.agent_name,
          dataset_id: form.dataset_id || undefined,
          is_trainable: true,
          base_model: a.base_model,
          filters,
        };
      });
      await connector.putAutoConfig(editingConfigId, integrationId, {
        enabled: form.enabled,
        schedule: form.schedule,
        regime: form.regime,
        regime_percent: form.regime_percent,
        period_days: form.period_days,
        history_depth: clampHistoryDepth(form.history_depth, DEFAULT_HISTORY_DEPTH),
        model_name_prefix: form.model_name_prefix,
        agents: agentsPayload,
      });
      setSuccess('Config updated');
      clearSuccess();
      setShowCreateForm(false);
      setEditingConfigId(null);
      setForm({
        enabled: true,
        schedule: 'daily',
        regime: 'no_overlap',
        regime_percent: 20,
        period_days: 30,
        history_depth: DEFAULT_HISTORY_DEPTH,
        model_name_prefix: '',
        dataset_id: '',
        agents: [],
      });
      await loadConfigs();
    } catch (e: any) {
      setError(getErrorMessage(e, 'Failed to update config'));
    } finally {
      setLoading(false);
    }
  };

  /** Get agent_id from current integration config (configurator) by agent name. Tries exact match, then name contains (e.g. DiaryWriteWorker vs DiaryWriteWorker1). */
  const getAgentIdFromConfigurator = (agentName: string): string | undefined => {
    if (!configFromConfigurator) return undefined;
    const coord = configFromConfigurator.coordinator;
    const coordName = coord?.name || 'Coordinator';
    if (coordName === agentName || (agentName === 'Coordinator' && coordName === 'Coordinator')) {
      const c = coord as { agent_id?: string; id?: string };
      const cid = c.agent_id ?? c.id;
      return cid ? String(cid).trim() || undefined : undefined;
    }
    const workers = configFromConfigurator.workers ?? [];
    let worker = workers.find((w) => (w.name || '') === agentName);
    if (!worker && agentName) {
      worker = workers.find((w) => {
        const wName = w.name || '';
        return wName === agentName || wName.startsWith(agentName) || agentName.startsWith(wName);
      });
    }
    return worker != null && worker.id != null ? String(worker.id) : undefined;
  };

  const openEditForm = async (config: AutoConfig) => {
    setError(null);
    try {
      const resp = await connector.getAutoConfig(config.id, integrationId!);
      const data = resp?.data ?? resp;
      const rawAgents = (data.agents ?? config.agents ?? []) as AutoConfigAgent[];
      const agents: FormAgent[] = rawAgents.map((a: AutoConfigAgent) => {
        const name = a.agent_name ?? 'Agent';
        const agentId = (a.agent_id && a.agent_id.trim()) || getAgentIdFromConfigurator(name);
        const bm = a.base_model ?? 'gpt-4.1-mini';
        return {
          agent_id: agentId,
          agent_name: name,
          dataset_id: typeof a.dataset_id === 'string' ? a.dataset_id : undefined,
          base_model: bm,
          is_trainable: a.is_trainable !== false && !isBaseModelUnsupportedForFineTuning(bm),
          filters: a.filters
            ? {
                reactions: a.filters.reactions ?? [],
                comment: a.filters.comment,
                groundtruth: a.filters.groundtruth,
              }
            : undefined,
        };
      });
      setForm({
        enabled: data.enabled !== false,
        schedule: data.schedule ?? 'daily',
        regime: data.regime ?? 'no_overlap',
        regime_percent: typeof data.regime_percent === 'number' ? data.regime_percent : 20,
        period_days: typeof data.period_days === 'number' ? data.period_days : 30,
        history_depth: clampHistoryDepth(
          typeof data.history_depth === 'number' ? data.history_depth : undefined,
          DEFAULT_HISTORY_DEPTH
        ),
        model_name_prefix: data.model_name_prefix ?? config.model_name_prefix ?? '',
        dataset_id: (rawAgents.find((a) => typeof a.dataset_id === 'string' && a.dataset_id.trim())?.dataset_id || ''),
        agents,
      });
      setEditingConfigId(config.id);
      setShowCreateForm(true);
    } catch (e: any) {
      setError(getErrorMessage(e, 'Failed to load config for editing'));
    }
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingConfigId(null);
    setForm({
      enabled: true,
      schedule: 'daily',
      regime: 'no_overlap',
      regime_percent: 20,
      period_days: 30,
      history_depth: DEFAULT_HISTORY_DEPTH,
      model_name_prefix: '',
      dataset_id: '',
      agents: [],
    });
  };

  /** Собираем реакции в массив для filters.reactions.
   *  Базово включаем всё: like/dislike + comment/groundtruth.
   */
  const reactionsToFilters = (name: string): { reactions: string[]; comment: boolean; groundtruth: boolean } => {
    const r = agentReactions[name];
    const likeOn = r?.like ?? true;
    const dislikeOn = r?.dislike ?? true;

    const list: string[] = [];
    if (likeOn) list.push('like');
    if (dislikeOn) list.push('dislike');

    return {
      reactions: list,
      comment: true,
      groundtruth: true,
    };
  };

  const openCreateForm = () => {
    setEditingConfigId(null);
    let agents: FormAgent[];
    if (configFromConfigurator) {
      agents = [];
      if (configFromConfigurator.coordinator && configFromConfigurator.coordinator.is_trainable !== false) {
        const name = configFromConfigurator.coordinator.name || 'Coordinator';
        const cm = configFromConfigurator.coordinator.model || 'gpt-4.1-mini';
        agents.push({
          agent_id: configFromConfigurator.coordinator.agent_id,
          agent_name: name,
          base_model: cm,
          is_trainable: !isBaseModelUnsupportedForFineTuning(cm),
          filters: reactionsToFilters(name),
        });
      }
      if (Array.isArray(configFromConfigurator.workers)) {
        configFromConfigurator.workers.forEach((w, i) => {
          const name = w.name || `Worker ${i + 1}`;
          const wm = w.model || 'gpt-4.1-mini';
          agents.push({
            agent_id: typeof w.id !== 'undefined' && w.id !== null ? String(w.id) : undefined,
            agent_name: name,
            base_model: wm,
            is_trainable: w.is_trainable !== false && !isBaseModelUnsupportedForFineTuning(wm),
            filters: reactionsToFilters(name),
          });
        });
      }
    } else {
      agents = integrationAgents.map((a: any) => {
        const bm = a.base_model || 'gpt-4.1-mini';
        return {
          agent_id: a.agent_id ?? a.id,
          agent_name: a.agent_name,
          base_model: bm,
          is_trainable: a.is_trainable_model !== false && !isBaseModelUnsupportedForFineTuning(bm),
          filters: a.agent_name ? reactionsToFilters(a.agent_name) : undefined,
        };
      });
    }
    const defaultDatasetId = connectorDatasets[0]?.id || '';
    setForm(prev => ({ ...prev, enabled: true, dataset_id: prev.dataset_id || defaultDatasetId, agents }));
    setShowCreateForm(true);
  };

  const toggleAgentTrainable = (idx: number) => {
    setForm((prev) => {
      const agents = [...prev.agents];
      const a = agents[idx];
      const bm = a.base_model || '';
      if (isBaseModelUnsupportedForFineTuning(bm)) {
        if (!a.is_trainable) return prev;
        agents[idx] = { ...a, is_trainable: false };
        return { ...prev, agents };
      }
      agents[idx] = { ...a, is_trainable: !a.is_trainable };
      return { ...prev, agents };
    });
  };

  const toggleAgentFilter = (idx: number, reaction: string) => {
    setForm(prev => {
      const agents = [...prev.agents];
      const current = agents[idx].filters?.reactions || [];
      const next = current.includes(reaction) ? current.filter(r => r !== reaction) : [...current, reaction];
      agents[idx] = { ...agents[idx], filters: normalizeAgentFilters(agents[idx].filters, next) };
      return { ...prev, agents };
    });
  };

  /** Установить лайк/дизлайк для одного агента (форма + подъём в родителя) */
  const setAgentReaction = (agentName: string, type: 'like' | 'dislike', value: boolean) => {
    const idx = form.agents.findIndex(a => a.agent_name === agentName);
    if (idx === -1) return;
    setForm(prev => {
      const agents = [...prev.agents];
      const current = agents[idx].filters?.reactions || [];
      let next = current.filter(r => r !== type);
      if (value) next = [...next, type];
      agents[idx] = { ...agents[idx], filters: normalizeAgentFilters(agents[idx].filters, next) };
      return { ...prev, agents };
    });
    const nextReactions = { ...agentReactions, [agentName]: { like: type === 'like' ? value : (agentReactions[agentName]?.like ?? false), dislike: type === 'dislike' ? value : (agentReactions[agentName]?.dislike ?? false) } };
    onAgentReactionsChange?.(nextReactions);
  };

  /** Лайк/дизлайк для всех агентов в форме */
  const setAllAgentsReaction = (type: 'like' | 'dislike', value: boolean) => {
    const names = form.agents.map(a => a.agent_name);
    setForm(prev => ({
      ...prev,
      agents: prev.agents.map(a => {
        const current = a.filters?.reactions || [];
        let next = current.filter(r => r !== type);
        if (value) next = [...next, type];
        return { ...a, filters: normalizeAgentFilters(a.filters, next) };
      }),
    }));
    const nextReactions = { ...agentReactions };
    names.forEach(name => {
      nextReactions[name] = { like: type === 'like' ? value : (nextReactions[name]?.like ?? false), dislike: type === 'dislike' ? value : (nextReactions[name]?.dislike ?? false) };
    });
    onAgentReactionsChange?.(nextReactions);
  };

  /** Сбросить реакции для всех агентов */
  const clearAllAgentsReactions = () => {
    setForm(prev => ({
      ...prev,
      agents: prev.agents.map(a => ({ ...a, filters: undefined })),
    }));
    const names = form.agents.map(a => a.agent_name);
    const nextReactions = { ...agentReactions };
    names.forEach(name => {
      nextReactions[name] = { like: false, dislike: false };
    });
    onAgentReactionsChange?.(nextReactions);
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusColor = (s: string | null) => {
    if (!s) return '#5B7893';
    switch (s.toLowerCase()) {
      case 'completed': case 'succeeded': return '#0072C6';
      case 'partially_succeeded': return '#FF8300';
      case 'running': case 'queued': return 'var(--brand-primary, #FF8300)';
      case 'failed': return '#FF8300';
      default: return '#5B7893';
    }
  };

  if (!integrationId) {
    return null;
  }

  return (
    <div id="auto-fine-tuning-panel-root" style={{ marginTop: 24 }}>
      {error && (
        <div style={{ padding: '10px 16px', background: '#FFFFFF', border: '1px solid #FFD9B3', borderRadius: 8, color: '#FF8300', fontSize: 13, marginBottom: 16 }}>
          <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>{error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#FF8300' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      {success && (
        <div style={{ padding: '10px 16px', background: '#FFFFFF', border: '1px solid #C6E0F7', borderRadius: 8, color: '#0072C6', fontSize: 13, marginBottom: 16 }}>
          <i className="fas fa-check-circle" style={{ marginRight: 6 }}></i>{success}
        </div>
      )}

      <div id="auto-ft-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0A2A47' }}>
          <i className="fas fa-magic" style={{ color: 'var(--brand-primary, #FF8300)', marginRight: 8 }}></i>
          Auto Fine-Tuning Configs
        </h3>
        <button
          id="auto-ft-new-config-btn"
          onClick={openCreateForm}
          style={{
            padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--brand-primary, #FF8300)', color: '#FFFFFF',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <i className="fas fa-plus"></i> New Config
        </button>
      </div>

      {/* Create / Edit Form */}
      {showCreateForm && (
        <div
          ref={editFormRef}
          className="aft-form-card"
          style={{ background: '#FFFFFF', border: '1px solid #E3F0FB', borderRadius: 12, padding: 24, marginBottom: 24 }}
        >
          <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: '#4A6683' }}>
            {editingConfigId ? 'Edit Auto Fine-Tuning Config' : 'Create Auto Fine-Tuning Config'}
          </h4>

          {editingConfigId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#4A6683' }}>
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))}
                />
                Enabled
              </label>
            </div>
          )}

          <div className="aft-form-grid" style={{ marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Model Name Prefix *</label>
              <input
                value={form.model_name_prefix}
                onChange={e => setForm(p => ({ ...p, model_name_prefix: e.target.value }))}
                style={{
                  ...inputStyle,
                  ...(form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH ? { borderColor: '#FF8300', outline: '1px solid #FF8300' } : {}),
                }}
                placeholder="auto-ft-prod"
                maxLength={MODEL_NAME_MAX_LENGTH}
                aria-invalid={form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH}
              />
              <div style={{ marginTop: 4, fontSize: 11, color: form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH ? '#FF8300' : '#5B7893' }}>
                {form.model_name_prefix.length} / {MODEL_NAME_MAX_LENGTH}
                {form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH && (
                  <span style={{ marginLeft: 6 }}>Exceeds maximum length</span>
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Dataset (optional, registry)</label>
              <select
                value={form.dataset_id}
                onChange={e => setForm(p => ({ ...p, dataset_id: e.target.value }))}
                style={inputStyle}
                disabled={datasetsLoading}
              >
                <option value="">{datasetsLoading ? 'Loading datasets…' : 'Dynamic path (no dataset_id)'}</option>
                {connectorDatasets.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label} ({d.id})
                  </option>
                ))}
              </select>
              <div style={{ marginTop: 4, fontSize: 11, color: '#5B7893' }}>
                Selected dataset id will be sent to all trainable agents in this config.
              </div>
            </div>
            <div>
              <label style={labelStyle}>Schedule</label>
              <select value={form.schedule} onChange={e => setForm(p => ({ ...p, schedule: e.target.value }))} style={inputStyle}>
                {SCHEDULES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Regime</label>
              <select value={form.regime} onChange={e => setForm(p => ({ ...p, regime: e.target.value }))} style={inputStyle}>
                {REGIMES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Regime % (0–90)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={regimePercentDraft}
                  onChange={(e) => setRegimePercentDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyRegimePercentDraft();
                    }
                  }}
                  style={{ ...inputStyle, flex: '1 1 140px', minWidth: 72 }}
                  placeholder="e.g. 20"
                  aria-label="Regime percent (draft)"
                />
                <button
                  type="button"
                  onClick={applyRegimePercentDraft}
                  style={{ ...smallBtnStyle, background: '#FFFFFF', color: '#0099FA', flexShrink: 0, padding: '8px 14px', fontSize: 12 }}
                >
                  Apply
                </button>
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: '#5B7893' }}>
                Current: {form.regime_percent}% — clear the field, type a new value, then Apply or Enter.
              </div>
            </div>
            <div>
              <label style={labelStyle}>Period (days, 1–365)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={periodDaysDraft}
                  onChange={(e) => setPeriodDaysDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyPeriodDaysDraft();
                    }
                  }}
                  style={{ ...inputStyle, flex: '1 1 140px', minWidth: 72 }}
                  placeholder="e.g. 30"
                  aria-label="Period in days (draft)"
                />
                <button
                  type="button"
                  onClick={applyPeriodDaysDraft}
                  style={{ ...smallBtnStyle, background: '#FFFFFF', color: '#0099FA', flexShrink: 0, padding: '8px 14px', fontSize: 12 }}
                >
                  Apply
                </button>
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: '#5B7893' }}>
                Current: {form.period_days} days — clear the field, type a new value, then Apply or Enter.
              </div>
            </div>
            <div>
              <label style={labelStyle}>History depth (messages)</label>
              <input
                type="number"
                min={HISTORY_DEPTH_MIN}
                max={HISTORY_DEPTH_MAX}
                value={numberInputDisplay(form.history_depth, DEFAULT_HISTORY_DEPTH)}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    history_depth: sanitizePositiveIntegerDraft(
                      e.target.value,
                      HISTORY_DEPTH_MIN,
                      HISTORY_DEPTH_MAX
                    ),
                  }))
                }
                onBlur={(e) =>
                  setForm((p) => ({
                    ...p,
                    history_depth: commitPositiveIntegerDraft(
                      e.target.value,
                      HISTORY_DEPTH_MIN,
                      HISTORY_DEPTH_MAX
                    ),
                  }))
                }
                style={inputStyle}
                aria-describedby="aft-history-depth-hint"
              />
              <div id="aft-history-depth-hint" style={{ marginTop: 4, fontSize: 11, color: '#5B7893' }}>
                Depth of messages used when building datasets for scheduled runs (default {DEFAULT_HISTORY_DEPTH}).
              </div>
            </div>
          </div>

          {/* Agents from configurator */}
          <div className="aft-agents-header">
            <label style={labelStyle}>Agents {configFromConfigurator ? '(from Configurator)' : ''}</label>
            {form.agents.length > 0 && (
              <div className="aft-reactions-bulk">
                <span style={{ fontSize: 12, color: '#5B7893' }}>Reactions for all:</span>
                <button
                  type="button"
                  onClick={() => setAllAgentsReaction('like', true)}
                  style={{ ...smallBtnStyle, background: '#E3F0FB', color: '#0072C6', display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Use likes for all agents"
                >
                  <i className="fas fa-thumbs-up"></i> Like all
                </button>
                <button
                  type="button"
                  onClick={() => setAllAgentsReaction('dislike', true)}
                  style={{ ...smallBtnStyle, background: '#E3F0FB', color: '#FF8300', display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Use dislikes for all agents"
                >
                  <i className="fas fa-thumbs-down"></i> Dislike all
                </button>
                <button
                  type="button"
                  onClick={clearAllAgentsReactions}
                  style={{ ...smallBtnStyle, background: '#F5F9FE', color: '#5B7893', display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Clear reactions for all"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
          {form.agents.length === 0 && (
            <div style={{ fontSize: 13, color: '#7F9AB3', marginBottom: 12 }}>
              {configFromConfigurator ? 'No trainable agents in configurator. Add Coordinator or Workers on the Integrations tab.' : 'No agents found for this integration.'}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {form.agents.map((agent, idx) => {
              const isLike = agent.filters?.reactions?.includes('like') ?? false;
              const isDislike = agent.filters?.reactions?.includes('dislike') ?? false;
              const isCoordinator = configFromConfigurator?.coordinator?.name === agent.agent_name;
              const commentEnabled = agent.filters?.comment ?? false;
              const groundtruthEnabled = agent.filters?.groundtruth ?? false;
              const filterBracket = formatAgentFiltersBracket(agent.filters);
              const ftBlocked = isBaseModelUnsupportedForFineTuning(agent.base_model);
              return (
                <div
                  key={`${agent.agent_name}-${idx}`}
                  className="aft-agent-row"
                  style={{
                    background: agent.is_trainable ? '#F5F9FE' : '#FFFFFF',
                    borderRadius: 10,
                    border: `1px solid ${agent.is_trainable ? 'rgba(255, 131, 0, 0.25)' : '#E3F0FB'}`,
                  }}
                >
                  <div className="aft-agent-main">
                    <i className={isCoordinator ? 'fas fa-user-tie' : 'fas fa-user-cog'} style={{ color: isCoordinator ? '#0099FA' : 'var(--brand-primary, #FF8300)', fontSize: 18, flexShrink: 0 }}></i>
                    <div className="aft-agent-name-block">
                      <div className="aft-agent-title" style={{ fontWeight: 600, fontSize: 14, color: '#0A2A47' }}>
                        {agent.agent_name}
                        {filterBracket && (
                          <span className="aft-agent-bracket" style={{ fontWeight: 500, color: '#5B7893', marginLeft: 6 }}>{filterBracket}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#5B7893', marginTop: 2 }}>{agent.base_model}</div>
                      {ftBlocked && (
                        <div style={{ fontSize: 11, color: '#B85400', marginTop: 4, lineHeight: 1.35 }}>
                          Fine-tuning is not supported for GPT-5 family models.
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className="aft-agent-trainable"
                    role="switch"
                    aria-checked={agent.is_trainable}
                    aria-disabled={ftBlocked}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      fontSize: 13,
                      color: '#4A6683',
                      cursor: ftBlocked ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      opacity: ftBlocked ? 0.65 : 1,
                    }}
                    title={
                      ftBlocked
                        ? 'GPT-5 family models cannot be fine-tuned. Use a supported base model in the configurator.'
                        : undefined
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (ftBlocked) return;
                      toggleAgentTrainable(idx);
                    }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        width: 48,
                        height: 24,
                        borderRadius: 12,
                        background: agent.is_trainable ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
                        transition: 'background 0.2s ease',
                        cursor: ftBlocked ? 'not-allowed' : 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: agent.is_trainable ? 26 : 2,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: '#FFFFFF',
                          boxShadow: '0 0 0 1px #E3F0FB',
                          transition: 'left 0.2s ease',
                        }}
                      />
                    </div>
                    <span>Include in Training Job</span>
                  </div>
                  <div className="aft-agent-actions">
                    <button
                      type="button"
                      onClick={() => setAgentReaction(agent.agent_name, 'like', !isLike)}
                      style={{
                        padding: '6px 10px',
                        border: 'none',
                        borderRadius: 6,
                        background: isLike ? '#0099FA' : '#E3F0FB',
                        color: isLike ? '#FFFFFF' : '#5B7893',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                      title="Use likes for training"
                    >
                      <i className="fas fa-thumbs-up"></i> Like
                    </button>
                    <button
                      type="button"
                      onClick={() => setAgentReaction(agent.agent_name, 'dislike', !isDislike)}
                      style={{
                        padding: '6px 10px',
                        border: 'none',
                        borderRadius: 6,
                        background: isDislike ? '#FF8300' : '#E3F0FB',
                        color: isDislike ? '#FFFFFF' : '#5B7893',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                      title="Use dislikes for training"
                    >
                      <i className="fas fa-thumbs-down"></i> Dislike
                    </button>
                    {/* Comment / Groundtruth flags */}
                    <button
                      type="button"
                      onClick={() => {
                        setForm(prev => {
                          const next = { ...prev };
                          const current = next.agents[idx];
                          const filters = current.filters ?? { reactions: [] };
                          next.agents = [...next.agents];
                          next.agents[idx] = {
                            ...current,
                            filters: { ...filters, comment: !commentEnabled },
                          };
                          return next;
                        });
                      }}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 999,
                        border: 'none',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: commentEnabled ? '#E3F0FB' : '#F5F9FE',
                        color: commentEnabled ? '#0072C6' : '#4A6683',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <i className="fas fa-comment-dots"></i>
                      Comments
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setForm(prev => {
                          const next = { ...prev };
                          const current = next.agents[idx];
                          const filters = current.filters ?? { reactions: [] };
                          next.agents = [...next.agents];
                          next.agents[idx] = {
                            ...current,
                            filters: { ...filters, groundtruth: !groundtruthEnabled },
                          };
                          return next;
                        });
                      }}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 999,
                        border: 'none',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: groundtruthEnabled ? '#E3F0FB' : '#F5F9FE',
                        color: groundtruthEnabled ? '#0A2A47' : '#4A6683',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <i className="fas fa-check-double"></i>
                      Groundtruth
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={closeForm} style={{ ...btnStyle, background: '#FFFFFF', color: '#4A6683', border: '1px solid #C6E0F7' }}>Cancel</button>
            {editingConfigId ? (
              <button
                onClick={handleUpdate}
                disabled={loading || form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH}
                style={{ ...btnStyle, background: 'var(--brand-primary, #FF8300)', color: '#FFFFFF', border: 'none', opacity: loading || form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH ? 0.6 : 1 }}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={loading || form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH}
                style={{ ...btnStyle, background: 'var(--brand-primary, #FF8300)', color: '#FFFFFF', border: 'none', opacity: loading || form.model_name_prefix.length > MODEL_NAME_MAX_LENGTH ? 0.6 : 1 }}
              >
                {loading ? 'Creating...' : 'Create Config'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Configs list */}
      <div id="auto-ft-configs-list">
      {loading && configs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#5B7893' }}>Loading...</div>
      ) : configs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#7F9AB3' }}>
          <i className="fas fa-cog" style={{ fontSize: 28, marginBottom: 8, display: 'block', opacity: 0.3 }}></i>
          <p>No auto fine-tuning configs yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {configs.map(cfg => (
            <div key={cfg.id} style={{
              background: '#FFFFFF', border: '1px solid #E3F0FB', borderRadius: 12, padding: '16px 20px',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: cfg.enabled ? '#0072C6' : '#C6E0F7',
                    flexShrink: 0,
                  }}></span>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{cfg.model_name_prefix || 'Unnamed'}</span>
                  <span style={{ fontSize: 12, color: '#5B7893', background: '#F5F9FE', padding: '2px 8px', borderRadius: 999 }}>
                    {cfg.schedule}
                  </span>
                  <span style={{ fontSize: 12, color: '#5B7893', background: '#F5F9FE', padding: '2px 8px', borderRadius: 999 }}>
                    {cfg.regime.replace(/_/g, ' ')}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => openEditForm(cfg)} style={{ ...smallBtnStyle, background: '#FFFFFF', color: '#0099FA' }}>
                    Edit
                  </button>
                  <button onClick={() => handleToggle(cfg)} style={{ ...smallBtnStyle, background: cfg.enabled ? '#F5F9FE' : '#FFFFFF', color: cfg.enabled ? '#B85400' : '#0072C6' }}>
                    {cfg.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => handleRun(cfg.id)}
                    disabled={runningConfigId === cfg.id || (cfg.model_name_prefix?.length ?? 0) > MODEL_NAME_MAX_LENGTH}
                    title={(cfg.model_name_prefix?.length ?? 0) > MODEL_NAME_MAX_LENGTH ? `Model name exceeds ${MODEL_NAME_MAX_LENGTH} characters` : undefined}
                    style={{ ...smallBtnStyle, background: '#FFFFFF', color: '#0099FA', opacity: runningConfigId === cfg.id || (cfg.model_name_prefix?.length ?? 0) > MODEL_NAME_MAX_LENGTH ? 0.5 : 1 }}
                  >
                    {runningConfigId === cfg.id ? 'Running...' : 'Run Now'}
                  </button>
                  <button onClick={() => handleDelete(cfg.id)} style={{ ...smallBtnStyle, background: '#FFFFFF', color: '#FF8300' }}>
                    Delete
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: '#5B7893' }}>
                <span><strong>Period:</strong> {cfg.period_days} days</span>
                <span>
                  <strong>History depth:</strong>{' '}
                  {typeof cfg.history_depth === 'number' ? cfg.history_depth : DEFAULT_HISTORY_DEPTH}
                </span>
                <span><strong>Regime %:</strong> {cfg.regime_percent}%</span>
                <span><strong>Agents:</strong> {cfg.agents?.length || 0}</span>
                {cfg.last_status && (
                  <span><strong>Last:</strong> <span style={{ color: statusColor(cfg.last_status), fontWeight: 600 }}>{cfg.last_status}</span></span>
                )}
                <span><strong>Next run:</strong> {formatDate(cfg.next_run_at)}</span>
                <span><strong>Last run:</strong> {formatDate(cfg.last_run_at)}</span>
              </div>

              {cfg.agents && cfg.agents.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {cfg.agents.map((a, i) => {
                    const bracket = formatAgentFiltersBracket(a.filters);
                    return (
                    <span key={i} style={{
                      fontSize: 11, padding: '3px 10px', borderRadius: 999,
                      background: a.is_trainable ? 'rgba(255, 131, 0, 0.1)' : '#F5F9FE',
                      color: a.is_trainable ? '#B85400' : '#5B7893',
                      border: `1px solid ${a.is_trainable ? 'rgba(255, 131, 0, 0.3)' : '#E3F0FB'}`,
                      fontWeight: 600,
                    }}>
                      {a.agent_name}
                      {bracket && (
                        <span style={{ marginLeft: 4, fontWeight: 400 }}>{bracket}</span>
                      )}
                    </span>
                  );
                  })}
                </div>
              )}

              {(cfg.last_error || cfg.last_error_details) && (
                <div style={{ fontSize: 12, color: '#B85400', background: '#F5F9FE', padding: '8px 12px', borderRadius: 8, border: '1px solid #FFD9B3' }}>
                  {cfg.last_error && (
                    <div style={{ marginBottom: cfg.last_error_details ? 6 : 0 }}>
                      <i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }}></i>{cfg.last_error}
                    </div>
                  )}
                  {cfg.last_error_details != null && (() => {
                    const d = cfg.last_error_details;
                    if (typeof d === 'string') {
                      return <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#B85400', fontFamily: 'inherit' }}>{d}</pre>;
                    }
                    if (typeof d === 'object' && d !== null) {
                      const obj = d as Record<string, unknown>;
                      const counts = obj.counts as Record<string, number> | undefined;
                      const failedAgents = obj.failed_agents as Array<{ agent_name?: string; error?: string }> | undefined;
                      const cancelledAgents = obj.cancelled_agents as string[] | Array<{ agent_name?: string }> | undefined;
                      const jobStatus = obj.job_status as string | undefined;
                      const jobError = obj.job_error as string | undefined;
                      const hasStructured = counts || (Array.isArray(failedAgents) && failedAgents.length > 0) || (Array.isArray(cancelledAgents) && cancelledAgents.length > 0) || jobStatus || jobError;
                      if (hasStructured) {
                        return (
                          <div style={{ margin: 0, fontSize: 11, color: '#B85400' }}>
                            {counts && typeof counts === 'object' && (
                              <div style={{ marginBottom: 6 }}>
                                <strong>Counts:</strong> {[counts.total != null && `total ${counts.total}`, counts.succeeded != null && `succeeded ${counts.succeeded}`, counts.failed != null && `failed ${counts.failed}`, counts.cancelled != null && `cancelled ${counts.cancelled}`, counts.running != null && `running ${counts.running}`, counts.queued != null && `queued ${counts.queued}`].filter(Boolean).join(', ')}
                              </div>
                            )}
                            {jobStatus && <div style={{ marginBottom: 4 }}><strong>Job status:</strong> {jobStatus}</div>}
                            {jobError && <div style={{ marginBottom: 6 }}><strong>Job error:</strong> {jobError}</div>}
                            {Array.isArray(failedAgents) && failedAgents.length > 0 && (
                              <div style={{ marginBottom: 4 }}>
                                <strong>Failed agents:</strong>
                                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                                  {failedAgents.map((a, i) => (
                                    <li key={i}>{typeof a === 'string' ? a : (a?.agent_name ? `${a.agent_name}: ${a.error || ''}` : String(a))}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {Array.isArray(cancelledAgents) && cancelledAgents.length > 0 && (
                              <div>
                                <strong>Cancelled agents:</strong>{' '}
                                {cancelledAgents.map((a, i) => (typeof a === 'object' && a && 'agent_name' in a ? (a as { agent_name?: string }).agent_name : String(a))).join(', ')}
                              </div>
                            )}
                          </div>
                        );
                      }
                    }
                    return <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#B85400', fontFamily: 'inherit' }}>{JSON.stringify(d, null, 2)}</pre>;
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </div>
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Auto Fine-Tuning Config"
        message="Delete this auto fine-tuning config? This cannot be undone."
        confirmText="Delete"
        isConfirmLoading={deleteLoading}
        onConfirm={confirmDeleteConfig}
        onCancel={() => setDeleteConfirm({ isOpen: false, configId: null })}
      />
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: '#4A6683', display: 'block', marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #C6E0F7',
  fontSize: 13, background: '#FFFFFF', boxSizing: 'border-box',
};

const btnStyle: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const smallBtnStyle: React.CSSProperties = {
  padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
};

export default AutoFineTuningPanel;
